import { apiClient } from '@/lib/api';
import axiosInstance from '@/lib/api/axios-instance';
import type {
  Disparo,
  EventoDeLaLinea,
  PaginaDeLaLinea,
  ProductosDelEvento,
  UltimasPorEstrategia,
} from '../types/corridas.types';
import { diaDeBolivia } from '../lib/rango-de-fechas';

export interface PedidoDeLaLinea {
  /** Instantes ISO (de `instantesDelRango`). */
  desde?: string;
  hasta?: string;
  estrategia?: string | null;
  disparos: Disparo[];
  cursor?: string | null;
}

/**
 * El nombre del archivo de un `Content-Disposition` (`filename*=UTF-8''…`
 * primero, después `filename="…"`). Sin cabecera o sin nombre: `porOmision`.
 */
export function nombreDelAdjunto(cabecera: unknown, porOmision: string): string {
  const texto = String(cabecera ?? '');
  const extendido = /filename\*=UTF-8''([^;]+)/i.exec(texto)?.[1];
  if (extendido) {
    try {
      return decodeURIComponent(extendido.trim());
    } catch {
      // Mal codificado: se prueba con el otro.
    }
  }
  return /filename="([^"]+)"/i.exec(texto)?.[1] ?? porOmision;
}

/** «corrida» o «recalculo», como lo espera la ruta. */
export const tipoDeRuta = (e: EventoDeLaLinea) => (e.tipo === 'CORRIDA' ? 'corrida' : 'recalculo');

/** Spec 275 (R2): las corridas de las estrategias y los recálculos de reglas. */
export const corridasApi = {
  linea: (p: PedidoDeLaLinea): Promise<PaginaDeLaLinea> => {
    const qs = new URLSearchParams();
    if (p.desde) qs.set('desde', p.desde);
    if (p.hasta) qs.set('hasta', p.hasta);
    if (p.estrategia) qs.set('estrategia', p.estrategia);
    qs.set('disparos', p.disparos.join(','));
    if (p.cursor) qs.set('cursor', p.cursor);
    return apiClient.get<PaginaDeLaLinea>(`/api/strategy-runs?${qs.toString()}`);
  },

  ultimas: async (n = 7): Promise<UltimasPorEstrategia> =>
    (
      await apiClient.get<{ porEstrategia: UltimasPorEstrategia }>(
        `/api/strategy-runs/ultimas?n=${n}`
      )
    ).porEstrategia,

  productos: (e: EventoDeLaLinea, estrategia: string | null): Promise<ProductosDelEvento> =>
    apiClient.get<ProductosDelEvento>(
      `/api/strategy-runs/${tipoDeRuta(e)}/${e.id}/productos${
        estrategia && e.tipo === 'RECALCULO' ? `?estrategia=${estrategia}` : ''
      }`
    ),

  /** D-R2-11: el .xlsx de lo filtrado, en el orden de la pantalla. */
  excel: async (e: EventoDeLaLinea, estrategia: string | null, ids: string[]): Promise<void> => {
    const respuesta = await axiosInstance.post(
      `/api/strategy-runs/${tipoDeRuta(e)}/${e.id}/productos/excel${
        estrategia && e.tipo === 'RECALCULO' ? `?estrategia=${estrategia}` : ''
      }`,
      { ids },
      { responseType: 'blob', timeout: 120000 }
    );
    // Recorrido del cierre de R2 (hallazgo 3): el servidor expone la cabecera
    // (`exposedHeaders` en `app.ts`); si igual no llega, el nombre lleva el día
    // de la corrida, como las otras descargas de la app que lo arman acá.
    const nombre = nombreDelAdjunto(
      respuesta.headers['content-disposition'],
      `${e.tipo === 'RECALCULO' ? 'recalculo' : 'corrida'}-${diaDeBolivia(new Date(e.cuando))}.xlsx`
    );
    const url = URL.createObjectURL(respuesta.data as Blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = nombre;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};
